import React, { useState } from 'react';
import { AgenticLiveTurnState, AgenticSessionSummaryView, AgenticThreadMessage } from '../../features/agentic-search/types';
import { SearchResult } from '../../services/api';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import SessionPanel from './SessionPanel';
import { hasSelectedSearchScope } from '../../features/agentic-search/scope';
import './AgenticChat.css';

interface AgenticChatThreadProps {
  sessionId: string | null;
  sessions: AgenticSessionSummaryView[];
  messages: AgenticThreadMessage[];
  liveTurn: AgenticLiveTurnState | null;
  processingSessionId?: string | null;
  isLoadingSessions: boolean;
  isLoadingSession: boolean;
  isProcessing: boolean;
  statusMessage: string;
  error: string | null;
  selectedIndexes: string[] | null;
  selectedRunIds: string[] | null;
  /** @deprecated Result tiles live in the external canvas. */
  tilesPerRow?: number;
  /** @deprecated Result tiles live in the external canvas. */
  showDetails?: boolean;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onNewSession: () => void;
  onSendMessage: (message: string) => Promise<void> | void;
  onSelectResultSet: (turnId: string, resultSetId: string | null) => void;
  onJumpToResults?: (turnId: string, resultSetId?: string | null) => void;
  onResultClick: (result: SearchResult) => void;
  /** @deprecated Result marker editing lives in the external canvas. */
  onMarkerChange?: (...args: any[]) => void;
  /** @deprecated Result tiles live in the external canvas. */
  onShowDetailsChange?: (show: boolean) => void;
  /** @deprecated Result tiles live in the external canvas. */
  onTilesPerRowChange?: (tiles: number) => void;
  onDismissError?: () => void;
  /** @deprecated Result selection is rendered in the external canvas. */
  selectedResultKey?: string | null;
  conversationScrollKey?: string;
  getConversationScrollPosition?: (key: string) => number;
  setConversationScrollPosition?: (key: string, scrollTop: number) => void;
}

export const AgenticChatThread: React.FC<AgenticChatThreadProps> = ({
  sessionId,
  sessions,
  messages,
  liveTurn,
  processingSessionId = null,
  isLoadingSessions,
  isLoadingSession,
  isProcessing,
  statusMessage,
  error,
  selectedIndexes,
  selectedRunIds,
  onSelectSession,
  onDeleteSession,
  onNewSession,
  onSendMessage,
  onSelectResultSet,
  onJumpToResults,
  onResultClick,
  onDismissError,
  conversationScrollKey,
  getConversationScrollPosition,
  setConversationScrollPosition,
}) => {
  const [chatInput, setChatInput] = useState('');
  const hasResults = messages.some((message) => (
    message.role === 'assistant' && message.resultSets.some((resultSet) => resultSet.results.length > 0)
  ));
  const hasSearchScope = hasSelectedSearchScope(selectedIndexes, selectedRunIds);
  const canSend = !isProcessing && (hasSearchScope || hasResults);

  const handleSend = async () => {
    const content = chatInput.trim();
    if (!content || !canSend) return;
    setChatInput('');
    try {
      await onSendMessage(content);
    } catch (sendError) {
      console.error('Failed to send agentic message:', sendError);
      setChatInput(content);
    }
  };

  return (
    <div className="agentic-chat-wrapper">
      <SessionPanel
        sessions={sessions}
        activeSessionId={sessionId}
        isLoading={isLoadingSessions}
        isLoadingSession={isLoadingSession}
        processingSessionId={processingSessionId}
        onSelectSession={onSelectSession}
        onDeleteSession={onDeleteSession}
        onNewSession={onNewSession}
        isCreatingSession={false}
      />

      <div className="agentic-chat-container">
        {error && (
          <div className="chat-error">
            <span className="error-message">{error}</span>
            {onDismissError && (
              <button className="dismiss-error" onClick={onDismissError}>×</button>
            )}
          </div>
        )}

        {isLoadingSession && (
          <div className="agentic-session-loading">
            <span className="typing-text">Loading session…</span>
          </div>
        )}

        <MessageList
          messages={messages}
          liveTurn={liveTurn}
          onResultClick={onResultClick}
          onSelectResultSet={onSelectResultSet}
          onJumpToResults={onJumpToResults}
          scrollKey={conversationScrollKey}
          getScrollPosition={getConversationScrollPosition}
          setScrollPosition={setConversationScrollPosition}
        />

        {isProcessing && !liveTurn && statusMessage && (
          <div className="typing-indicator-container">
            <div className="typing-indicator">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="typing-text">{statusMessage}</span>
            </div>
          </div>
        )}

        <ChatInput
          value={chatInput}
          onChange={setChatInput}
          onSend={handleSend}
          inputDisabled={isProcessing}
          sendDisabled={!canSend}
          sendDisabledTitle="Select at least one index or extraction run to start a new search"
          placeholder={
            isProcessing
              ? statusMessage || 'Processing…'
              : !hasSearchScope && hasResults
                ? 'Ask a follow-up about the current results...'
                : !hasSearchScope
                  ? 'Select at least one index or extraction run to start a new search...'
                : 'Ask about your video content...'
          }
        />
      </div>
    </div>
  );
};

export default AgenticChatThread;
